import React, { useState, useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import { 
  X, UploadCloud, FileSpreadsheet, DownloadCloud, 
  ChevronRight, CheckCircle2, AlertCircle, Loader2, GraduationCap,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Select from "react-select";
import {
  customerInModalSelectStyles,
  formatCustomerOptionLabel,
  filterCustomerOption,
  modalSelectPortalProps,
} from "../../../components/ui/customerSelectInModal";
import {
  classSelectStyles,
  classSelectPortalProps,
  formatClassOptionLabel,
  filterClassOption,
  mapClassesToSelectOptions,
} from "../../../components/ui/classSelectInModal";
import { showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";
import { importStudents } from "../../../api/elearning/students/students";
import { getAllBranches_Array, getBranchById } from "../../../api/hrm/branches/info";
import HrmDashboardBranchPicker, {
  branchSubtitle,
} from "../../hrm/shared/hrmDashboardBranchSelect";
import {
  getStudentMetaSchools,
  getStudentMetaClasses,
} from "../../../api/elearning/students/students";

const SESSION_SUPER_ROLES = [
  "super admin",
  "superadmin",
  "administrator",
  "system administrator",
];

function readSessionUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

function readUserBranchId() {
  const u = readSessionUser();
  return u.branch?.id || u.branch_id || null;
}

function isSuperAdminSession() {
  const u = readSessionUser();
  const roles = Array.isArray(u.roles) ? u.roles : [];
  return roles
    .map((r) => String(r || "").trim().toLowerCase())
    .some((r) => SESSION_SUPER_ROLES.includes(r));
}

const COLUMN_HELP = [
  "Mã học sinh — tuỳ chọn; nếu nhập phải gồm đúng 10 chữ số.",
  "Họ tên — bắt buộc.",
  "Số điện thoại — khuyến nghị bắt buộc (nếu trùng sẽ báo lỗi).",
  "Ngày sinh — tuỳ chọn; chấp nhận dd/MM/yyyy (vd. 20/03/2016), yyyy-MM-dd, hoặc ô ngày Excel.",
  "Giới tính — nam/nữ hoặc male/female (để trống sẽ không nhập dữ liệu).",
  "Email — tuỳ chọn.",
  "Địa chỉ — tuỳ chọn.",
  "Tên phụ huynh — tuỳ chọn.",
  "SĐT phụ huynh — tuỳ chọn.",
];

const StudentImportModal = ({ isOpen, onClose, onRefresh }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [classes, setClasses] = useState([]);
  const [schools, setSchools] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [branchId, setBranchId] = useState(null);
  const [branchAffiliation, setBranchAffiliation] = useState("affiliated");
  /** "" | "school" | "center" — để trống cho đến khi user chọn Đối tượng */
  const [unit, setUnit] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [classId, setClassId] = useState(null);
  const [importErrors, setImportErrors] = useState([]);

  const formatImportError = (e) => {
    if (typeof e === "string") return e;
    const line = e?.line != null ? `Dòng ${e.line}` : "Lỗi dữ liệu";
    const msg = e?.message || "Dữ liệu không hợp lệ";
    return `${line}: ${msg}`;
  };

  useEffect(() => {
    if (!isOpen) return;

    const init = async () => {
      setLoading(true);
      const currentBranchId = readUserBranchId();
      const superAdmin = isSuperAdminSession();
      setIsSuperAdmin(superAdmin);
      setClassId(null);
      setSchoolId("");
      setClasses([]);
      setSchools([]);
      setImportErrors([]);

      try {
        if (superAdmin) {
          const rows = await getAllBranches_Array({ is_active: true, limit: 500 });
          const opts = (rows || []).map((b) => ({
            value: b.id,
            label: b.name,
            affiliation_type: b.affiliation_type || "affiliated",
          }));
          setBranchOptions(opts);
          setBranchId(null);
          setBranchAffiliation("affiliated");
          setUnit("");
          return;
        }

        setBranchId(currentBranchId || null);
        if (!currentBranchId) return;
        const br = await getBranchById(currentBranchId);
        const aff = br?.affiliation_type || "affiliated";
        setBranchAffiliation(aff);
        setUnit(aff === "franchise" ? "center" : "");
      } catch {
        setBranchOptions([]);
        setClasses([]);
        setSchools([]);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const effectiveBranchId = branchId;
    if (!effectiveBranchId) {
      setSchools([]);
      return;
    }
    if (!isSuperAdmin || unit !== "school") {
      setSchools([]);
      return;
    }

    const loadSchools = async () => {
      setLoading(true);
      try {
        const d = await getStudentMetaSchools(effectiveBranchId);
        const allSchools = d.schools || [];

        // Chỉ giữ các trường đang có ít nhất 1 lớp học trong chi nhánh
        const checks = await Promise.all(
          allSchools.map(async (school) => {
            try {
              const classRes = await getStudentMetaClasses({
                branch_id: effectiveBranchId,
                school_id: school.id,
              });
              const hasClasses = Array.isArray(classRes?.classes) && classRes.classes.length > 0;
              return hasClasses ? school : null;
            } catch {
              return null;
            }
          })
        );

        setSchools(checks.filter(Boolean));
      } catch {
        setSchools([]);
      } finally {
        setLoading(false);
      }
    };

    loadSchools();
  }, [isOpen, branchId, isSuperAdmin, unit]);

  useEffect(() => {
    if (!isOpen) return;
    if (isSuperAdmin && !branchId) {
      setClasses([]);
      return;
    }
    if (!unit) {
      setClasses([]);
      return;
    }

    const loadClasses = async () => {
      setLoading(true);
      try {
        const params = { unit: unit === "center" ? "center" : "school" };
        if (isSuperAdmin && branchId) {
          params.branch_id = branchId;
          if (unit === "school" && schoolId) params.school_id = schoolId;
        }
        const d = await getStudentMetaClasses(params);
        setClasses(d.classes || []);
      } catch {
        setClasses([]);
      } finally {
        setLoading(false);
      }
    };

    loadClasses();
  }, [isOpen, branchId, unit, schoolId, isSuperAdmin]);

  const classOptions = useMemo(
    () => mapClassesToSelectOptions(classes),
    [classes]
  );
  const schoolOptions = useMemo(
    () =>
      schools.map((s) => ({
        value: s.id,
        label: s.name,
        ward: s.ward || s.ward_name || "",
      })),
    [schools]
  );
  const branchSelectOptions = useMemo(
    () => [
      { value: "", label: "Chọn chi nhánh", sub: "Tên chi nhánh • Loại chi nhánh • Người đứng đầu" },
      ...branchOptions.map((b) => ({
        value: b.value,
        label: b.label,
        sub: branchSubtitle(b),
      })),
    ],
    [branchOptions]
  );
  const isFranchiseBranch = branchAffiliation === "franchise";
  const canPickUnit = isSuperAdmin || (!isSuperAdmin && !isFranchiseBranch);
  const showSchoolSelect = isSuperAdmin && unit === "school";
  const schoolSignal = useMemo(
    () => ({
      matchedSchoolCount: schoolOptions.length,
      branchTotalCount: schoolOptions.length,
    }),
    [schoolOptions]
  );
  const classSignal = useMemo(() => {
    if (!unit) return "Chọn đối tượng để tải danh sách lớp";
    const total = classOptions.length;
    if (unit === "school") {
      const selectedSchool = schoolOptions.find((opt) => opt.value === schoolId);
      return selectedSchool
        ? `Lớp học khớp với đối tác "${selectedSchool.label}": ${total}`
        : `Lớp học khớp theo điều kiện hiện tại: ${total}`;
    }
    return `Lớp học tại trung tâm khớp điều kiện: ${total}`;
  }, [classOptions, unit, schoolId, schoolOptions]);

  const resetImport = () => {
    setStep(1);
    setFile(null);
    setClassId(null);
    setSchoolId("");
    setUnit("");
    setImportErrors([]);
    setLoading(false);
  };

  const needsUnitPick = canPickUnit && !unit;
  const canProceedStep1 =
    !needsUnitPick && !(isSuperAdmin && !branchId);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && (selectedFile.name.endsWith(".xlsx") || selectedFile.name.endsWith(".xls") || selectedFile.name.endsWith(".csv"))) {
      setFile(selectedFile);
      setImportErrors([]);
    } else {
      showErrorToast("Vui lòng chọn file Excel (.xlsx, .xls) hoặc .csv");
    }
  };

  const handleDownloadTemplate = () => {
    const rows = [
      [
        "Mã học sinh",
        "Họ tên",
        "Số điện thoại",
        "Ngày sinh",
        "Giới tính",
        "Email",
        "Địa chỉ",
        "Tên phụ huynh",
        "SĐT phụ huynh",
      ],
      ["HS001", "Nguyễn Văn An", "0901234567", "2015-05-15", "nam", "an@email.com", "123 Đường ABC", "Hồ Thị Bình", "0912345678"],
      ["", "Trần Thị Bích", "0918765432", "2016-03-20", "nữ", "", "456 XYZ", "Trần Văn Cường", "0987654321"],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Danh sách học viên");
    XLSX.writeFile(wb, "mau_hoc_vien.xlsx");
  };

  const handleUpload = async () => {
    if (!file) return showErrorToast("Vui lòng chọn file danh sách");
    setLoading(true);
    try {
      const res = await importStudents(file, classId || null);
      showSuccessToast(res.message || `Import thành công ${res.imported} học viên`);
      if (Array.isArray(res.errors) && res.errors.length > 0) {
        setImportErrors(res.errors.map(formatImportError));
        const preview = res.errors.slice(0, 5);
        const previewText = preview.map((e) => formatImportError(e)).join(" | ");
        const suffix =
          res.errors.length > preview.length
            ? ` | ... và ${res.errors.length - preview.length} lỗi khác.`
            : "";
        showErrorToast(`Có ${res.errors.length} dòng lỗi. ${previewText}${suffix}`);
      } else {
        setImportErrors([]);
      }
      onRefresh?.();
      if (!Array.isArray(res.errors) || res.errors.length === 0) {
        onClose();
        resetImport();
      }
    } catch (err) {
      setImportErrors([err.response?.data?.message || "Lỗi khi upload dữ liệu"]);
      showErrorToast(err.response?.data?.message || "Lỗi khi upload dữ liệu");
    } finally {
      setLoading(false);
    }
  };

  const StepLabel = ({ num, title, active }) => (
    <div className={`flex items-center gap-2 ${active ? "opacity-100" : "opacity-40"}`}>
      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${active ? "bg-red-600 text-white" : "bg-gray-200"}`}>
        {num}
      </span>
      <span className="text-[11px] font-bold uppercase tracking-wider">{title}</span>
    </div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[110]"
            onClick={onClose}
          />
          <motion.div 
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 flex items-center justify-center z-[120] p-4 pointer-events-none"
          >
            <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden pointer-events-auto flex flex-col">
              
              {/* Header */}
              <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <div>
                  <h2 className="text-2xl font-black text-gray-800 flex items-center gap-3">
                    <UploadCloud className="text-red-600" /> IMPORT HỌC VIÊN
                  </h2>
                  <p className="text-xs text-gray-500 font-medium mt-1">Làm theo các bước để tải danh sách lên hệ thống</p>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-white rounded-full transition-all shadow-sm border border-transparent hover:border-gray-200">
                  <X size={20} />
                </button>
              </div>

              {/* Stepper Indicator */}
              <div className="px-8 py-4 bg-white border-b border-gray-50 flex items-center justify-between">
                <StepLabel num={1} title="Cấu hình" active={step === 1} />
                <ChevronRight size={14} className="text-gray-300" />
                <StepLabel num={2} title="Chọn File" active={step === 2} />
                <ChevronRight size={14} className="text-gray-300" />
                <StepLabel num={3} title="Hoàn tất" active={step === 3} />
              </div>

              {/* Body Content */}
              <div className="p-8 overflow-y-auto max-h-[60vh] custom-scrollbar">
                {step === 1 && (
                  <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="space-y-2">
                      {isSuperAdmin && (
                        <div className="space-y-2">
                          <label className="text-[11px] font-black text-gray-400 uppercase">
                            Chi nhánh
                          </label>
                          <HrmDashboardBranchPicker
                            instanceId="student-import-branch"
                            options={branchSelectOptions}
                            value={branchId || ""}
                            onChange={(nextVal) => {
                              const nextBranchId = nextVal || null;
                              const picked = branchOptions.find((b) => b.value === nextBranchId);
                              const aff = picked?.affiliation_type || "affiliated";
                              setBranchId(nextBranchId);
                              setBranchAffiliation(aff);
                              setUnit(aff === "franchise" ? "center" : "");
                              setSchoolId("");
                              setClassId(null);
                              setClasses([]);
                              setSchools([]);
                            }}
                            aria-label="Chọn chi nhánh import học viên"
                          />
                        </div>
                      )}

                      {canPickUnit && (
                        <div className="space-y-2">
                          <label className="text-[11px] font-black text-gray-400 uppercase">
                            Đối tượng
                          </label>
                          <div className="grid grid-cols-2 gap-3 pt-1">
                            <button
                              type="button"
                              onClick={() => {
                                setUnit("school");
                                setSchoolId("");
                                setClassId(null);
                                setClasses([]);
                              }}
                              className={`flex flex-col items-center p-3 rounded-2xl border-2 transition-all ${
                                unit === "school"
                                  ? "border-red-600 bg-red-50 text-red-700"
                                  : "border-gray-100 bg-white text-gray-500 hover:border-gray-300"
                              }`}
                              disabled={isFranchiseBranch}
                            >
                              <span className="text-xl mb-1">🏫</span>
                              <span className="text-[11px] font-black uppercase tracking-wider">
                                Tại trường học
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setUnit("center");
                                setSchoolId("");
                                setClassId(null);
                                setClasses([]);
                              }}
                              className={`flex flex-col items-center p-3 rounded-2xl border-2 transition-all ${
                                unit === "center"
                                  ? "border-red-600 bg-red-50 text-red-700"
                                  : "border-gray-100 bg-white text-gray-500 hover:border-gray-300"
                              }`}
                            >
                              <span className="text-xl mb-1">🏛️</span>
                              <span className="text-[11px] font-black uppercase tracking-wider">
                                Tại trung tâm
                              </span>
                            </button>
                          </div>
                          {!unit && (
                            <p className="text-[10px] font-semibold text-amber-700">
                              Vui lòng chọn đối tượng để tải danh sách lớp học.
                            </p>
                          )}
                        </div>
                      )}

                      {showSchoolSelect && (
                        <div className="space-y-2">
                          <label className="text-[11px] font-black text-gray-400 uppercase flex items-center gap-2">
                            <GraduationCap size={14} /> Đơn vị (trường học)
                          </label>
                          <Select
                            styles={customerInModalSelectStyles}
                            options={schoolOptions}
                            placeholder="Chọn đơn vị trường học..."
                            value={schoolOptions.find((o) => o.value === schoolId) || null}
                            onChange={(opt) => {
                              setSchoolId(opt?.value || "");
                              setClassId(null);
                              setClasses([]);
                            }}
                            isClearable
                            isDisabled={!branchId}
                            formatOptionLabel={formatCustomerOptionLabel}
                            filterOption={filterCustomerOption}
                            {...modalSelectPortalProps}
                          />
                          <p className="mt-1 text-[10px] font-semibold tracking-wide text-gray-500">
                            {`Khớp loại trường: ${schoolSignal.matchedSchoolCount} | Tổng đối tác chi nhánh: ${schoolSignal.branchTotalCount}`}
                          </p>
                        </div>
                      )}

                      <label className="text-[11px] font-black text-gray-400 uppercase flex items-center gap-2">
                        <GraduationCap size={14} /> Gán vào lớp (tùy chọn)
                      </label>
                      <Select
                        instanceId="student-import-class"
                        styles={classSelectStyles}
                        options={classOptions}
                        placeholder="Tìm hoặc chọn lớp (để trống nếu chỉ thêm học viên)..."
                        value={classOptions.find((o) => o.value === classId) || null}
                        onChange={(opt) => setClassId(opt?.value || null)}
                        isClearable
                        isSearchable
                        filterOption={filterClassOption}
                        formatOptionLabel={formatClassOptionLabel}
                        noOptionsMessage={({ inputValue }) =>
                          inputValue
                            ? "Không tìm thấy lớp phù hợp"
                            : "Không có lớp trong điều kiện hiện tại"
                        }
                        maxMenuHeight={280}
                        menuPlacement="auto"
                        isDisabled={
                          !unit ||
                          (isSuperAdmin && (!branchId || (showSchoolSelect && !schoolId)))
                        }
                        {...classSelectPortalProps}
                      />
                      <p className="mt-1 text-[10px] font-semibold tracking-wide text-gray-500">
                        {classSignal}
                      </p>
                    </div>
                    <div className="p-4 bg-blue-50 border border-blue-100 rounded-2xl flex gap-3">
                      <AlertCircle className="text-blue-500 shrink-0" size={18} />
                      <p className="text-[11px] text-blue-700 leading-relaxed font-medium">
                        File cần có cột: <strong>họ tên</strong> (bắt buộc). Khuyến nghị thêm{" "}
                        <strong>số điện thoại</strong>. Cột <strong>mã học sinh</strong> là tuỳ chọn.
                        Các cột khác: ngày sinh, giới tính, email, địa chỉ, tên phụ huynh, SĐT phụ huynh.
                        {isSuperAdmin && !branchId && " Vui lòng chọn chi nhánh trước."}
                        {canPickUnit && !unit && " Vui lòng chọn đối tượng (trường học hoặc trung tâm)."}
                        {classId && " Học viên import sẽ tự động gắn vào lớp đã chọn."}
                      </p>
                    </div>

                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-6 text-center py-4 animate-in fade-in slide-in-from-right-4 duration-500">
                    <div className="border-2 border-dashed border-gray-200 rounded-[2rem] p-10 hover:border-red-400 hover:bg-red-50/30 transition-all group relative">
                      <input 
                        type="file" 
                        accept=".xlsx, .xls, .csv"
                        className="absolute inset-0 opacity-0 cursor-pointer z-10" 
                        onChange={handleFileChange}
                      />
                      <div className="space-y-4">
                        <div className="w-16 h-16 bg-red-100 text-red-600 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                          {file ? <CheckCircle2 size={32} /> : <FileSpreadsheet size={32} />}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-800">{file ? file.name : "Kéo thả file vào đây hoặc click để chọn"}</p>
                          <p className="text-xs text-gray-400 mt-1">Hỗ trợ định dạng .xlsx, .xls, .csv</p>
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleDownloadTemplate}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-red-100 bg-red-50/50 text-red-700 text-xs font-bold uppercase tracking-wide hover:bg-red-50 transition-all"
                    >
                      <DownloadCloud size={16} />
                      Tải file mẫu Excel (.xlsx)
                    </button>

                    <p className="text-[10px] text-gray-500 text-center leading-snug">
                      File mẫu được xuất trực tiếp dạng Excel (.xlsx) để giữ nguyên cấu trúc và dữ liệu mẫu.
                    </p>

                    <div className="rounded-2xl border border-slate-100 bg-slate-50/80 p-4 text-left">
                      <p className="text-[11px] font-bold text-slate-700 uppercase tracking-wide mb-2">
                        Cột trong mẫu (dòng 1 = tiêu đề)
                      </p>
                      <ul className="text-[11px] text-slate-600 leading-relaxed space-y-1.5 list-disc pl-4">
                        {COLUMN_HELP.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </div>

                    {importErrors.length > 0 && (
                      <div className="rounded-2xl border border-rose-200 bg-rose-50/80 p-4 text-left">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-[11px] font-bold text-rose-700 uppercase tracking-wide">
                            Chi tiết lỗi import
                          </p>
                          <span className="text-[11px] font-semibold text-rose-600">
                            {importErrors.length} lỗi
                          </span>
                        </div>
                        <p className="text-[11px] text-rose-700 mt-1">
                          Kiểm tra và sửa các dòng dưới đây rồi import lại.
                        </p>
                        <div className="mt-3 max-h-40 overflow-y-auto rounded-xl border border-rose-100 bg-white p-3">
                          <ul className="space-y-1.5 text-[11px] text-rose-800 leading-relaxed">
                            {importErrors.map((errLine, idx) => (
                              <li key={`${idx}-${errLine}`}>- {errLine}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-8 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
                <button 
                  onClick={() => step === 1 ? onClose() : setStep(step - 1)}
                  className="px-6 py-3 font-bold text-gray-500 hover:bg-gray-200 rounded-2xl transition-all"
                >
                  {step === 1 ? "HUỶ BỎ" : "QUAY LẠI"}
                </button>
                <button 
                  onClick={() => {
                    if (step === 1) {
                      if (!canProceedStep1) {
                        if (isSuperAdmin && !branchId) {
                          showErrorToast("Vui lòng chọn chi nhánh.");
                        } else if (needsUnitPick) {
                          showErrorToast("Vui lòng chọn đối tượng (tại trường học hoặc tại trung tâm).");
                        }
                        return;
                      }
                      setStep(2);
                    } else handleUpload();
                  }}
                  disabled={loading || (step === 1 && !canProceedStep1)}
                  className="px-10 py-3 bg-red-600 text-white rounded-2xl font-bold flex items-center gap-2 shadow-lg shadow-red-100 hover:bg-red-700 transition-all active:scale-95 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : (step === 1 ? "TIẾP THEO" : "BẮT ĐẦU IMPORT")}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default StudentImportModal;